import {
  apiGet,
  apiPost,
  apiPostForm,
  apiPut,
} from "@softmax/observatory-client/client";
import type { components } from "@softmax/observatory-client/generated/public-schema";
import type { components as internalComponents } from "@softmax/observatory-client/generated/internal-schema";
import { ApiError } from "@softmax/observatory-client/transport";

import type {
  StorePageContent,
  StorePageDraft,
  StorePageEditResult,
  UploadedImage,
} from "./types";

type Write = { slug: string; idempotencyKey: string };
type RevisionWrite = Write & { revisionId: string };
type Read = { slug: string; signal: AbortSignal };

export const storePageApi = {
  getStorePageDraft({ slug, signal }: Read): Promise<StorePageDraft | null> {
    return apiGet<StorePageDraft>(
      `/v2/coworlds/${encodeURIComponent(slug)}/store-page/draft`,
      { elevatedPrivileges: false, signal },
    ).catch((error: unknown) => {
      if (error instanceof ApiError && error.status === 404) return null;

      throw error;
    });
  },

  async getStorePageRevisions({ slug, signal }: Read) {
    const result = await apiGet<
      components["schemas"]["StorePageRevisionListPublic"]
    >(`/v2/coworlds/${encodeURIComponent(slug)}/store-page/revisions`, {
      elevatedPrivileges: false,
      signal,
    });
    return result.entries;
  },

  saveDraft({
    slug,
    content,
    baseRevisionId,
    idempotencyKey,
    note = null,
  }: Write & {
    content: StorePageContent;
    baseRevisionId: string | null;
    note?: string | null;
  }) {
    return apiPut<StorePageEditResult>(
      `/v2/coworlds/${encodeURIComponent(slug)}/store-page/draft`,
      {
        content,
        base_revision_id: baseRevisionId,
        idempotency_key: idempotencyKey,
        note,
      } satisfies components["schemas"]["StorePageEditRequest"],
      { elevatedPrivileges: false },
    );
  },

  publish({
    slug,
    baseRevisionId,
    idempotencyKey,
  }: Write & { baseRevisionId: string }) {
    return apiPost<StorePageEditResult>(
      `/v2/coworlds/${encodeURIComponent(slug)}/store-page/publish`,
      {
        base_revision_id: baseRevisionId,
        idempotency_key: idempotencyKey,
      } satisfies components["schemas"]["StorePagePublishRequest"],
      { elevatedPrivileges: false },
    );
  },

  approve({ slug, revisionId, idempotencyKey }: RevisionWrite) {
    return apiPost<StorePageEditResult>(
      `/v2/coworlds/${encodeURIComponent(slug)}/store-page/approve`,
      {
        revision_id: revisionId,
        idempotency_key: idempotencyKey,
      } satisfies internalComponents["schemas"]["StorePageApproveRequest"],
      { elevatedPrivileges: true },
    );
  },

  reject({
    slug,
    revisionId,
    reason,
    idempotencyKey,
  }: RevisionWrite & { reason: string }) {
    return apiPost<StorePageEditResult>(
      `/v2/coworlds/${encodeURIComponent(slug)}/store-page/reject`,
      {
        revision_id: revisionId,
        reason,
        idempotency_key: idempotencyKey,
      } satisfies internalComponents["schemas"]["StorePageRejectRequest"],
      { elevatedPrivileges: true },
    );
  },

  revert({ slug, revisionId, idempotencyKey }: RevisionWrite) {
    return apiPost<StorePageEditResult>(
      `/v2/coworlds/${encodeURIComponent(slug)}/store-page/revert`,
      {
        revision_id: revisionId,
        idempotency_key: idempotencyKey,
      } satisfies components["schemas"]["StorePageRevertRequest"],
      { elevatedPrivileges: false },
    );
  },

  uploadImage({ slug, file }: { slug: string; file: File }) {
    const body = new FormData();
    body.set("file", file);
    return apiPostForm<UploadedImage>(
      `/v2/coworlds/${encodeURIComponent(slug)}/store-page/media`,
      body,
      { elevatedPrivileges: false },
    );
  },
};
